import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";

const CLOUDS = [
  { top: "14%", w: 420, h: 120, dur: 48, delay: 0 },
  { top: "38%", w: 300, h: 90, dur: 36, delay: -14 },
  { top: "62%", w: 520, h: 150, dur: 58, delay: -30 },
  { top: "24%", w: 240, h: 70, dur: 30, delay: -22 },
];

export function Clouds() {
  return (
    <div className="absolute inset-0 overflow-hidden" aria-hidden="true" data-testid="auth-clouds">
      {CLOUDS.map((c, i) => (
        <span key={i} className="cloud" style={{ top: c.top, width: c.w, height: c.h, animationDuration: `${c.dur}s`, animationDelay: `${c.delay}s` }} />
      ))}
    </div>
  );
}

const JET = "M0,-13 C1.6,-13 2.2,-10 2.2,-6 L2.2,-2 L12,4 L12,6.5 L2.2,3.2 L2.2,8 L5.2,10.6 L5.2,12.4 L0,11 L-5.2,12.4 L-5.2,10.6 L-2.2,8 L-2.2,3.2 L-12,6.5 L-12,4 L-2.2,-2 L-2.2,-6 C-2.2,-10 -1.6,-13 0,-13 Z";
// Jets fly in a band between the logo and the headline so they never cross the text. Positions are % of the band.
const SKY = [
  { x1: -8, y1: 80, x2: 108, y2: 20, dur: 12, delay: 0, s: 0.95 },
  { x1: 108, y1: 25, x2: -8, y2: 70, dur: 15, delay: -5, s: 0.65 },
  { x1: -8, y1: 50, x2: 108, y2: 52, dur: 18, delay: -11, s: 0.5 },
  { x1: 108, y1: 75, x2: -8, y2: 25, dur: 13, delay: -8, s: 0.8 },
  { x1: -8, y1: 30, x2: 108, y2: 75, dur: 16, delay: -2, s: 0.6 },
  { x1: 108, y1: 55, x2: -8, y2: 38, dur: 20, delay: -14, s: 0.45 },
];

function useSize(ref) {
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const ro = new ResizeObserver(([e]) => setSize({ w: e.contentRect.width, h: e.contentRect.height }));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [ref]);
  return size;
}

export function Jets({ className = "inset-0" }) {
  const ref = useRef(null);
  const { w, h } = useSize(ref);
  return (
    <div ref={ref} className={`absolute overflow-hidden pointer-events-none ${className}`} aria-hidden="true" data-testid="auth-jets">
      {w > 0 && SKY.map((j, i) => {
        const px = Math.round(Math.min(j.s * 50, h * 0.42));
        const angle = (Math.atan2((j.y2 - j.y1) * h, (j.x2 - j.x1) * w) * 180) / Math.PI + 90;
        return (
          <svg key={i} className="sky-jet" viewBox="-14 -14 28 28" width={px} height={px}
            style={{ "--x1": `${j.x1}%`, "--y1": `${j.y1}%`, "--x2": `${j.x2}%`, "--y2": `${j.y2}%`, margin: `${-px / 2}px 0 0 ${-px / 2}px`,
              animationDuration: `${j.dur}s`, animationDelay: `${j.delay}s` }}>
            <g transform={`rotate(${angle})`} opacity={0.5 + j.s * 0.45}>
              <path d={JET} fill="#0B1530" opacity="0.35" transform="translate(1.5,2)" />
              <path d={JET} fill="#FFFFFF" stroke="#C6A55C" strokeWidth="0.8" />
            </g>
          </svg>
        );
      })}
    </div>
  );
}

export function Blade() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true" data-testid="auth-blade">
      <div className="blade">
        <span className="blade-glow" />
        <span className="blade-edge" />
      </div>
    </div>
  );
}

const PATH = "M -20 520 C 160 470, 260 300, 420 260 S 700 150, 860 40";

export function FlightPath() {
  const reduce = useReducedMotion();
  return (
    <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 800 900" preserveAspectRatio="xMidYMid slice" aria-hidden="true" data-testid="auth-flight-path">
      <motion.path d={PATH} fill="none" stroke="#C6A55C" strokeWidth="1.4" strokeLinecap="round" strokeDasharray="2 7"
        initial={{ pathLength: 0, opacity: 0 }} animate={reduce ? { pathLength: 1, opacity: 0.5 } : { pathLength: [0, 1, 1], opacity: [0, 0.75, 0] }}
        transition={reduce ? { duration: 0 } : { duration: 9, times: [0, 0.75, 1], ease: "easeInOut", repeat: Infinity }} />
      <g className="flight-jet" style={{ offsetPath: `path("${PATH}")` }}>
        <path d="M 10 0 L -6 -5 L -3 0 L -6 5 Z" fill="#E6CF94" />
        <circle r="9" fill="#C6A55C" opacity="0.18" />
      </g>
    </svg>
  );
}

const lineUp = (delay) => ({
  initial: { opacity: 0, y: 26 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] },
});

export function RevealWords({ text, className, start = 0.5, still = false }) {
  if (still) return <h1 className={className}>{text}</h1>;
  return (
    <h1 className={className} aria-label={text}>
      {text.split(" ").map((w, i) => (
        <motion.span key={i} className="inline-block mr-[0.28em]" aria-hidden="true" {...lineUp(start + i * 0.12)}>{w}</motion.span>
      ))}
    </h1>
  );
}

export const Reveal = ({ delay = 0, className, children, testId, still = false }) => (
  still ? <div className={className} data-testid={testId}>{children}</div>
    : <motion.div className={className} data-testid={testId} {...lineUp(delay)}>{children}</motion.div>
);

export function SpinLogo({ className, testId, still = false }) {
  const cls = `w-auto self-start mix-blend-screen ${className}`;
  if (still) return <img src="/brand/logo-globe-poster.jpg" alt="JetSet Group Inc" className={cls} data-testid={testId} />;
  return (
    <motion.video poster="/brand/logo-globe-poster.jpg" autoPlay muted loop playsInline aria-label="JetSet Group Inc"
      className={cls} initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }} data-testid={testId}>
      <source src="/brand/logo-globe-spin.webm" type="video/webm" />
      <source src="/brand/logo-globe-spin.mp4" type="video/mp4" />
    </motion.video>
  );
}
